import { useCustomFields } from '@/hooks/useCustomFields';
import { Badge } from '@/components/ui/Badge';
import type { CustomFieldEntityType } from '@/types';
import { format, parseISO } from 'date-fns';
import { ExternalLink } from 'lucide-react';

interface CustomFieldsDisplayProps {
  entityType: CustomFieldEntityType;
  customFields?: Record<string, any>;
}

export function CustomFieldsDisplay({ entityType, customFields }: CustomFieldsDisplayProps) {
  const { data: definitions } = useCustomFields(entityType);

  if (!definitions?.length || !customFields) return null;

  const activeFields = definitions.filter((d) => d.isActive);
  const fieldsWithValues = activeFields.filter((d) => customFields[d.name] != null && customFields[d.name] !== '');

  if (!fieldsWithValues.length) return null;

  return (
    <div className="space-y-2">
      {fieldsWithValues.map((field) => {
        const value = customFields[field.name];
        return (
          <div key={field.id}>
            <p className="text-xs text-grey-400 mb-0.5">{field.label}</p>
            <div className="text-sm text-secondary">
              <FormattedValue fieldType={field.fieldType} value={value} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FormattedValue({ fieldType, value }: { fieldType: string; value: any }) {
  switch (fieldType) {
    case 'BOOLEAN':
      return <span>{value ? 'Yes' : 'No'}</span>;
    case 'DATE':
      try {
        return <span>{format(parseISO(String(value)), 'MMM d, yyyy')}</span>;
      } catch {
        return <span>{String(value)}</span>;
      }
    case 'URL':
      return (
        <a
          href={String(value)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1"
        >
          {String(value).replace(/^https?:\/\//, '').slice(0, 40)}
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    case 'SELECT':
      return <Badge variant="outline" size="sm">{String(value)}</Badge>;
    case 'NUMBER':
      return <span>{Number(value).toLocaleString()}</span>;
    default:
      return <span>{String(value)}</span>;
  }
}
